/**
 * API sözleşme tipleri. Kaynak: services/api (drf-spectacular). `npm run gen:api` ile
 * packages/contracts/openapi.json'dan üretilen schema.d.ts bunların yerini alır; o zamana kadar elle tutulur.
 * Tutarlar daima kanonik ondalık METİN ("1250.50"): float yok.
 */

export type Role = 'member' | 'admin';

export interface Me {
  user: { id: string; username: string; full_name: string; email: string };
  organization: { id: string; name: string };
  role: Role;
  session: { id: string; kind: 'web' | 'native'; expires_at: string } | null;
}

export type TimePrecision = 'DATE' | 'MINUTE' | 'SECOND';

export interface CandidateFields {
  amount: string | null;
  document_date: string | null;
  document_time: string | null;
  time_precision: TimePrecision | null;
  vendor_name: string | null;
  vendor_tax_no: string | null;
  document_no: string | null;
  terminal_ref: string | null;
  category: string | null;
  description: string | null;
  payment_method?: PaymentMethod | null;
  payment_due_date?: string | null;
  bank_ref_masked?: string | null;
}

/** Ödeme şekli: kredi kartı, banka kartı, elden (nakit), veresiye (ileri tarihli ödeme). */
export type PaymentMethod = 'CREDIT_CARD' | 'DEBIT_CARD' | 'CASH' | 'CREDIT_SALE';
export const PAYMENT_LABELS: Record<PaymentMethod, string> = {
  CREDIT_CARD: 'Kredi kartı', DEBIT_CARD: 'Banka kartı', CASH: 'Elden ödeme', CREDIT_SALE: 'Veresiye',
};
export interface Settlement { paid_on: string; note: string | null; actor_name: string; created_at: string }

export type CandidateStatus = 'DRAFT' | 'UPLOADING' | 'OCR_PENDING' | 'REVIEW_REQUIRED' | 'READY' | 'SUBMITTED' | 'REJECTED';

export interface FileInfo {
  id: string;
  role: 'ORIGINAL' | 'PREVIEW' | 'PROOF';
  mime_type: string;
  size_bytes: number;
  sha256: string;
  width: number | null;
  height: number | null;
  is_ready: boolean;
  created_at?: string;
  url: string | null;
}

export interface OcrInfo {
  id: string;
  status: 'RUNNING' | 'DONE' | 'FAILED';
  fields: {
    amount_kurus: number | null;
    amount_confidence: number;
    amount_candidates: { value: string; kurus: number; confidence: number; line: string }[];
    document_date: string | null;
    document_time: string | null;
    time_precision: TimePrecision;
    vendor_name: string | null;
    description: string | null;
    ambiguous: string[];
    /** false: el yazısı ya da okunaksız; hiçbir alan doldurulmadı */
    readable?: boolean;
  } | null;
  confidence: { mean_word: number; amount: number; date: number; ambiguous: string[] } | null;
  model_version: string;
  error_code: string | null;
}

export interface DuplicateCase {
  id: string;
  candidate_id: string;
  candidate_version: number;
  matched_receipt_id: string;
  level: 'EXACT' | 'STRONG' | 'WEAK';
  evidence: Record<string, unknown>;
  decision: 'PENDING' | 'SEPARATE' | 'DUPLICATE' | 'NEED_BETTER_IMAGE';
  decision_reason: string | null;
  decided_at: string | null;
  created_at: string;
}

export interface Candidate {
  id: string;
  client_draft_id: string;
  version: number;
  status: CandidateStatus;
  duplicate_status: string;
  fields: CandidateFields;
  ocr: OcrInfo | null;
  files: FileInfo[] | null;
  duplicate_cases?: DuplicateCase[];
  submitted_receipt_id: string | null;
  last_error_code: string | null;
  created_at: string;
  updated_at: string;
}

export type ReceiptStatus = 'ACTIVE' | 'CANCELLED' | 'REFUNDED_PARTIAL' | 'REFUNDED_FULL';

export interface Receipt {
  id: string;
  period_id: string;
  owner_id: string;
  owner_name: string;
  document_date: string;
  document_time: string | null;
  time_precision: TimePrecision;
  vendor_name: string | null;
  category: string;
  description: string;
  amount: string;
  status: ReceiptStatus;
  current_revision: number;
  payment_method: PaymentMethod | null;
  payment_due_date: string | null;
  created_at: string;
  net_contribution?: string;
}

/** Vadeli (veresiye) ödeme satırı. */
export interface CreditRow extends Receipt { days_left: number | null; settlement: Settlement | null }

export interface ReceiptDetail extends Receipt {
  vendor_tax_no: string | null;
  document_no: string | null;
  terminal_ref: string | null;
  bank_ref_masked: string | null;
  files: FileInfo[];
  revisions: { revision_no: number; old_fields: Record<string, unknown>; new_fields: Record<string, unknown>; reason: string; actor_id: string; created_at: string }[];
  ledger: { id: string; type: 'EXPENSE' | 'ADJUSTMENT' | 'CANCEL' | 'REFUND'; amount: string; period_id: string; created_at: string }[];
  refunds: { id: string; refund_date: string; amount: string; reason: string; period_id: string; created_at: string }[];
  cancellation: { reason: string; created_at: string } | null;
  refunded_total: string;
  can_edit: boolean;
  settlement: Settlement | null;
}

export interface Period {
  id: string;
  year_month: string;
  status: 'OPEN' | 'CLOSED';
  closed_at: string | null;
  closed_by: string | null;
  snapshot_id: string | null;
  month_ended: boolean;
  pending?: { count: number; items: { candidate_id: string; owner_id: string; status: string; document_date: string | null }[] };
}

export interface Totals { gross_kurus: string; cancel_kurus: string; refund_kurus: string; net_kurus: string }

export interface Dashboard {
  period: { id: string; year_month: string; status: 'OPEN' | 'CLOSED' };
  totals: Totals;
  counts: { active: number; cancelled: number; refunds: number };
  my_net: string;
  my_active_count: number;
  recent: { id: string; owner_name: string; document_date: string; amount: string; category: string; status: ReceiptStatus }[];
  as_of: string;
}

export interface PersonSummary {
  period: { id: string; year_month: string; status: string };
  user_id: string;
  name: string | null;
  totals: Totals;
  active_count: number;
  rows: { receipt_id: string; document_date: string; description: string; category: string; amount_kurus: number; period_effect_kurus: number; status: ReceiptStatus; source_period: string | null }[];
}

/** Kullanıcı. email, password_set, code_expires_at, last_login_at yalnızca yöneticiye gelir. */
export interface Member {
  user_id: string; username: string; full_name: string; role: Role; is_active: boolean; created_at: string;
  email?: string; password_set?: boolean; code_expires_at?: string | null; last_login_at?: string | null;
}

/** Yönetici kullanıcı tanımladığında / şifre sıfırladığında: kod yalnızca bu yanıtta görünür. */
export interface SetupCode { user_id: string; email: string; full_name: string; role?: Role; setup_code: string; code_expires_at: string }

export interface CategorySuggestion { category: string; count: number; reason: 'vendor' | 'popular'; vendor_count?: number }
export interface CategoryRow {
  id: string; name: string; key: string; receipt_count: number;
  merged: { id: string; name: string; receipt_count: number }[];
}

/** Gider istatistiği: tutarlar API biçiminde ("1234.56"). */
export interface MoneyTotals { gross: string; refund: string; cancel: string; net: string }
export interface CategoryStat extends MoneyTotals { key: string; name: string; count: number; share: number }
export interface ExpenseStats {
  scope: 'month' | 'year';
  year: string;
  year_month: string | null;
  years: string[];
  totals: MoneyTotals;
  categories: CategoryStat[];
  receipt_count: number;
  period_status?: 'OPEN' | 'CLOSED' | null;
  months?: { year_month: string; net: string; status: 'OPEN' | 'CLOSED' | null }[];
}
export interface ExpenseCategoryDetail {
  scope: 'month' | 'year'; year: string; year_month: string | null; key: string; name: string;
  totals: MoneyTotals;
  rows: {
    receipt_id: string; document_date: string; vendor_name: string | null; owner_name: string; category: string;
    description: string; amount: string; status: ReceiptStatus; effect: MoneyTotals; source_period: string | null;
  }[];
}

export interface Invitation { id: string; email: string; role: Role; expires_at: string; accepted_at: string | null; revoked_at: string | null; created_at: string }

export interface ReportJob {
  id: string;
  period_id: string;
  year_month: string;
  scope: 'SELF' | 'PERSON' | 'ALL';
  filters: Record<string, string>;
  include_photos: boolean;
  as_of: string;
  status: 'QUEUED' | 'RUNNING' | 'DONE' | 'FAILED';
  content_hash: string | null;
  error_code: string | null;
  created_at: string;
  finished_at: string | null;
  download_url?: string;
}

export interface RefundRow { id: string; source_receipt_id: string; owner_id: string; owner_name: string; period_id: string; year_month: string; refund_date: string; amount: string; reason: string; partial: boolean; created_at: string }
export interface CancellationRow { id: string; receipt_id: string; owner_id: string; owner_name: string; period_id: string; amount: string; reason: string; created_at: string }
export interface NotificationRow {
  id: string; type: string; title: string; body: string; url: string;
  period_id: string | null; payload: Record<string, string>; read_at: string | null; created_at: string;
}
export interface SessionRow { id: string; kind: 'web' | 'native'; device_label: string | null; created_at: string; last_used_at: string; current: boolean }
